import { useMemo } from "react";
import { Stack, TablePagination } from "@mui/material";
import { LegendComponent } from "../../../../../../common/components/legend_component";
import { LegendIcon } from "../../../../../../common/components/legend_icon";
import { LegendItemComponent } from "../../../../../../common/components/legend_item_component";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import { Documento } from "../../../../domain/models/documento";
import { GenerateDocumentoColumnsDefinition, GenerateEntregueColumnsDefinition } from "../columns";
import { DocumentoEntregue } from "../../../../domain/models/documento_entregue";
import { UseFormReturn } from "react-hook-form";
import { FuncionarioForm } from "../../../schemas/Funcionarios_cadastro/funcionario_schema";

import PrimaryButton from "../../../../../../common/components/primary_button";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import CascateList from "../../../../../../common/components/cascate_list";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";

/**
 * TabDocumentosContent
 */

type Props = {
  loading: boolean,

  hookForm: UseFormReturn<FuncionarioForm>,

  documentos: Documento[],
  documentosEntregues: DocumentoEntregue[],

  documentosPage: number,
  documentosRowsPerPage: number,
  documentosTotalItems: number,

  documentosEntreguesPage: number;
  documentosEntreguesRowsPerPage: number;
  documentosEntreguesTotalItems: number;

  onDocumentosPageChange: (newPage: number) => void,
  onDocumentosRowsPerPageChange: (newRowsPerPage: number) => void,

  onDocumentosEntreguesPageChange: (newPage: number) => void,
  onDocumentosEntreguesRowsPerPageChange: (newRowsPerPage: number) => void,

  onSetCadastroDocumentoModalOpenState: (id: number | null, state: boolean) => void,
  onSetDeletarDocumentoModalOpenState: (id: number | null, state: boolean) => void,

  onDeleteAllDocumentos: (openState: boolean) => void,

  onDownload: (idFuncionarioArquivo: number, imageName: string) => void,
  onDownloadAllDocumentos: () => void,
};

export const TabDocumentosContent = ({
  loading,

  hookForm,

  documentos,
  documentosEntregues,

  documentosPage,
  documentosRowsPerPage,
  documentosTotalItems,

  documentosEntreguesPage,
  documentosEntreguesRowsPerPage,
  documentosEntreguesTotalItems,

  onDocumentosPageChange,
  onDocumentosRowsPerPageChange,

  onDocumentosEntreguesPageChange,
  onDocumentosEntreguesRowsPerPageChange,

  onSetCadastroDocumentoModalOpenState,
  onSetDeletarDocumentoModalOpenState,

  onDeleteAllDocumentos,

  onDownload,
  onDownloadAllDocumentos,
}: Props) => {
  const [formDocumentosEntregues] = hookForm.watch(['documentosEntregues']);

  const finalDocumentosEntregues = useMemo((): DocumentoEntregue[] => {
    return [...documentosEntregues.map((x) => ({ ...x, ...formDocumentosEntregues?.find(y => y.idDocumento === x.idDocumento) }))];
  }, [documentosEntregues, formDocumentosEntregues]);

  const documentColumnsDefinition = useMemo(() => {
    return GenerateDocumentoColumnsDefinition({
      onActionEdit: (id) => onSetCadastroDocumentoModalOpenState(id ?? null, true),
      onActionDelete: (id) => onSetDeletarDocumentoModalOpenState(id ?? null, true),
      onDownload: onDownload,
    });
  }, [
    onSetCadastroDocumentoModalOpenState,
    onSetDeletarDocumentoModalOpenState,
    onDownload,
  ]);

  const entreguesColumnsDefinition = useMemo(() => {
    return GenerateEntregueColumnsDefinition({
      onToggleEntregue: (idDocumentoEntregue, idDocumento, state) => {
        const docEnts = hookForm.getValues('documentosEntregues') ?? [];
        const newDocEnts = docEnts.filter(x => x.idDocumento !== idDocumento);
        const oldNaoSeAplica = finalDocumentosEntregues?.find(x => x.idDocumento === idDocumento)?.naoSeAplica;

        hookForm.setValue('documentosEntregues', [...newDocEnts, {
          idDocumentoEntregue,
          idDocumento,
          entregue: state,
          naoSeAplica: oldNaoSeAplica ?? false,
        }]);
      },
      onToggleNaoSeAplica: (idDocumentoEntregue, idDocumento, state) => {
        const docEnts = hookForm.getValues('documentosEntregues') ?? [];
        const newDocEnts = docEnts.filter(x => x.idDocumento !== idDocumento);
        const oldEntregue = finalDocumentosEntregues?.find(x => x.idDocumento === idDocumento)?.entregue;

        hookForm.setValue('documentosEntregues', [...newDocEnts, {
          idDocumentoEntregue,
          idDocumento,
          naoSeAplica: state,
          entregue: oldEntregue ?? false,
        }]);
      },
    });
  }, [hookForm, finalDocumentosEntregues]);

  return (
    <>
      <>
        <div className="div flex mt-8 justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
          <div className="flex gap-2 items-center w-full">
            <h1 className="text-xl font-medium text-black dark:text-white">Documentos diversos</h1>
          </div>

          <div className="flex w-full justify-end  gap-2">
            <PrimaryButton
              className="whitespace-nowrap"
              startIcon={<DynamicIcon nameIcon="Add" size={25} className="text-white" />}
              sx={{ paddingX: 3 }}
              onClick={() => onSetCadastroDocumentoModalOpenState(null, true)}>
              Novo documento
            </PrimaryButton>

            <PrimaryButton
              buttonColor='red'
              startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}
              className="whitespace-nowrap"
              sx={{ paddingX: 3 }}
              onClick={() => onDownloadAllDocumentos()}>
              Baixar arquivos
            </PrimaryButton>

            <PrimaryButton
              buttonColor='yellow'
              startIcon={<DynamicIcon nameIcon="Delete" size={25} className="text-black" />}
              className="whitespace-nowrap"
              sx={{ paddingX: 3 }}
              onClick={() => onDeleteAllDocumentos(true)}>
              Deletar arquivos
            </PrimaryButton>
          </div>
        </div>
      </>

      <Stack spacing={0} className="mt-4">
        <div className="w-full flex flex-row justify-between items-center">
          <LegendComponent>
            <LegendItemComponent
              icon={<LegendIcon color='yellow' title='E' />}
              label='Em dia' />

            <LegendItemComponent
              icon={<LegendIcon color='red' title='V' />}
              label='Vencido' />
          </LegendComponent>

          <TablePagination
            component="div"
            count={documentosTotalItems}
            page={documentosPage - 1}
            onPageChange={(_, p) => onDocumentosPageChange(p)}
            rowsPerPageOptions={[10, 25, 50, 100]}
            rowsPerPage={documentosRowsPerPage}
            onRowsPerPageChange={(e) => onDocumentosRowsPerPageChange(parseInt(e.target.value))} />

        </div>

        <StyledDataGrid
          tableName="tab_habilidades_content_1"
          disableColumnMenu={false}
          getRowId={(row: Documento) => row.idDocumento}
          columns={documentColumnsDefinition}
          loading={loading}
          rows={documentos}
          rowCount={documentos.length}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationMode="server"
          paginationModel={{
            page: documentosPage,
            pageSize: documentosRowsPerPage,
          }} />

        <TablePagination
          component="div"
          count={documentosTotalItems}
          page={documentosPage - 1}
          onPageChange={(_, p) => onDocumentosPageChange(p)}
          rowsPerPageOptions={[10, 25, 50, 100]}
          rowsPerPage={documentosRowsPerPage}
          onRowsPerPageChange={(e) => onDocumentosRowsPerPageChange(parseInt(e.target.value))} />

      </Stack>

      <div className="w-full mt-4">
        <CascateList
          title="Selecione documentos entregues"
          defaultOpen={false}>
          <div className="w-full">
            <Stack spacing={0}>
              <StyledDataGrid
                tableName="tab_documentos_content_2"
                disableColumnMenu={false}
                getRowId={(row: DocumentoEntregue) => row.idDocumento}
                columns={entreguesColumnsDefinition}
                loading={loading}
                rows={finalDocumentosEntregues}
                rowCount={finalDocumentosEntregues.length - 1}
                pageSizeOptions={[10, 25, 50, 100]}
                paginationMode="server"
                paginationModel={{
                  page: documentosEntreguesPage,
                  pageSize: documentosEntreguesRowsPerPage,
                }} />

              <TablePagination
                component="div"
                count={documentosEntreguesTotalItems}
                page={documentosEntreguesPage - 1}
                onPageChange={(_, p) => onDocumentosEntreguesPageChange(p)}
                rowsPerPageOptions={[10, 25, 50, 100]}
                rowsPerPage={documentosEntreguesRowsPerPage}
                onRowsPerPageChange={(e) => onDocumentosEntreguesRowsPerPageChange(parseInt(e.target.value))} />

            </Stack>
          </div>
        </CascateList>
      </div>

      <>
        <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mt-4 mb-4">
          <FormContainer title={"Observações"}>
            <>
              <div className="col-span-2">
                <DefaultFormInput
                  name={'observacaoDocumento'}
                  label={"Observações"}
                  multiline={true}
                  rows={7}
                  reactHookForm={hookForm} />
              </div>
            </>
          </FormContainer>
        </div>
      </>
    </>
  );
}